import type { NotificationPublic, ReadEntityNotificationsType, UnreadNotificationsCount } from '@repo/types/notifications';
import type { InfiniteData } from '@tanstack/react-query';

import { useMutation, useQueryClient } from '@tanstack/react-query';

import { NotificationKey } from './keys';
import { NotificationService } from './notificationService';

/**
 * Checks if a notification matches the given entity criteria.
 *
 * @param notification - The notification to check
 * @param entityId - The entity ID to match (can be string or number)
 * @param type - The notification type to match
 * @returns True if the notification matches both entityId and type
 */
function isMatchingEntityNotification(notification: NotificationPublic, entityId: string | number, type: string): boolean {
  // Compare as strings to handle both string and number entity IDs
  return String(notification.entityId) === String(entityId) && notification.type === type;
}

/**
 * Updates a notification's read status if it matches the entity criteria.
 *
 * @param notification - The notification to potentially update
 * @param entityId - The entity ID to match (can be string or number)
 * @param type - The notification type to match
 * @returns The notification with updated read status if matched
 */
function markNotificationAsReadIfMatching(notification: NotificationPublic, entityId: string | number, type: string): NotificationPublic {
  const shouldMarkAsRead = isMatchingEntityNotification(notification, entityId, type);

  return {
    ...notification,
    read: shouldMarkAsRead ? true : notification.read,
  };
}

/**
 * Decrements the unread count for a specific notification type.
 *
 * @param count - The current unread count object
 * @param targetType - The type to decrement
 * @returns Updated count object with decremented value for matching type
 */
function decrementCountForType(count: UnreadNotificationsCount, targetType: string): UnreadNotificationsCount {
  if (count.type !== targetType) {
    return count;
  }

  const currentCount = Number(count.count);
  const newCount = Math.max(0, currentCount - 1);

  return {
    ...count,
    count: String(newCount),
  };
}

/**
 * Cancels all pending notification-related queries to prevent race conditions.
 *
 * @param queryClient - The React Query client instance
 */
async function cancelNotificationQueries(queryClient: ReturnType<typeof useQueryClient>): Promise<void> {
  await Promise.all([
    queryClient.cancelQueries({
      queryKey: [NotificationKey.NOTIFICATIONS_INFINITE],
      exact: false,
    }),
    queryClient.cancelQueries({
      queryKey: [NotificationKey.UNREAD_NOTIFICATIONS_COUNT],
      exact: false,
    }),
  ]);
}

/**
 * Optimistically updates the notifications cache to mark entity notifications as read.
 *
 * @param queryClient - The React Query client instance
 * @param data - The entity notification data containing entityId and type
 */
function updateNotificationsCache(queryClient: ReturnType<typeof useQueryClient>, data: ReadEntityNotificationsType): void {
  queryClient.setQueriesData<InfiniteData<Array<NotificationPublic>>>(
    {
      queryKey: [NotificationKey.NOTIFICATIONS_INFINITE],
      exact: false,
    },
    (currentData) => {
      if (!currentData) {
        return undefined;
      }

      return {
        ...currentData,
        pages: currentData.pages.map((page) => page.map((notification) => markNotificationAsReadIfMatching(notification, data.entityId, data.type))),
      };
    }
  );
}

/**
 * Optimistically updates the unread notifications count cache.
 *
 * @param queryClient - The React Query client instance
 * @param targetType - The notification type to decrement count for
 */
function updateUnreadCountCache(queryClient: ReturnType<typeof useQueryClient>, targetType: string): void {
  queryClient.setQueriesData<UnreadNotificationsCount[] | undefined>(
    {
      queryKey: [NotificationKey.UNREAD_NOTIFICATIONS_COUNT],
      exact: false,
    },
    (currentCounts) => {
      if (!currentCounts) {
        return undefined;
      }

      return currentCounts.map((count) => decrementCountForType(count, targetType));
    }
  );
}

/**
 * Hook for marking all notifications for a specific entity as read.
 *
 * This is useful when the user views a specific entity (e.g., opens a chat)
 * and all related notifications should be marked as read at once.
 *
 * @returns A mutation object for marking entity notifications as read
 *
 * @example
 * // Get the mutation function
 * const mutation = usePostReadEntityNotifications();
 *
 * // Mark all chat notifications for a specific conversation as read
 * mutation.mutate({ entityId: 'conversation-123', type: NotificationType.Chat });
 */
export function usePostReadEntityNotifications() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: ReadEntityNotificationsType) => NotificationService.readEntityNotification(data).then((response) => response.data),

    onMutate: async (data: ReadEntityNotificationsType) => {
      // Cancel any in-flight queries to prevent race conditions
      await cancelNotificationQueries(queryClient);

      // Optimistically update both caches
      updateNotificationsCache(queryClient, data);
      updateUnreadCountCache(queryClient, data.type);
    },
  });
}
